import React from 'react';
import { toast } from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { addKnownAssetsData, getWatcherAssetsData, getWatcherAssetsInitials } from '../../redux/actions/watcherActions';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';
import jsPDF from 'jspdf';

import Content from '../../components/Content';
import Table, { SortableTh } from '../../components/Table';
import useSort from '../../components/Table/useSort';
import TextInput from '../../components/TextInput';
import Pagination from '../../components/Pagination';
import Loader from '../../components/Loader';
import NetworkError from '../../components/Error';
import TextArea from '../../components/Modal/TextArea';

import WatcherAssetsStyles from './WatcherAssets.module.css';
import IpTrafficModal from './IpTrafficModal';
import NetworkTrafficModal from './NetworkMap';

const FRONTEND_WATCHER_ASSETS_ERROR_CODES = {
	INITIAL_DATA_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	WATCHER_DATA_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	COMPANY_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	ASSET_FILE_UPLOAD_FAILED: ErrorCodes.FILE_PROCESSING_FAILED,
	ASSET_ADD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	NETZONE_ADD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	MOVE_ASSETS_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	ASSET_UPDATE_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	CSV_EXPORT_FAILED: ErrorCodes.FILE_PROCESSING_FAILED,
	PDF_EXPORT_FAILED: ErrorCodes.FILE_PROCESSING_FAILED,
};

const buildWatcherAssetsSupportError = (errorCode, pageInfo, sourceMessage) =>
	formatFrontendSupportError({
		errorCode,
		pageInfo,
		sourceMessage,
	});

const WatcherAssetsScreen = () => {
	const dispatch = useDispatch();
	const userLogin = useSelector((state) => state.userLogin);

	const [ userRole, setUserRole ] = React.useState('');
	const [ userCompany, setUserCompany ] = React.useState('');
	const [ selectedWatcher, setSelectedWatcher ] = React.useState('');
	const [ searchKnownAssetsKey, setSearchKnownAssetsKey ] = React.useState('');
	const [ searchDetectedAssetsKey, setSearchDetectedAssetsKey ] = React.useState('');

	// Deep-link from the dashboard's "New & Unknown Assets" Investigate: ?q=<ip>
	// filters both asset tables to that device so it loads straight to the asset.
	const location = useLocation();
	React.useEffect(() => {
		const q = new URLSearchParams(location.search).get('q');
		if (q) { setSearchDetectedAssetsKey(q); setSearchKnownAssetsKey(q); }
	}, [ location.search ]);

		// Replace simple modal state with enhanced form state
		const [ showAddKnownAssetsForm, setShowAddKnownAssetsForm ] = React.useState(false);

	const [ dataLoading, setDataLoading ] = React.useState(false);
	const [ watcherDataInitials, setWatcherDataInitials ] = React.useState({});
	const [ watcherData, setWatcherData ] = React.useState({});

	const [ knownAssetsTotalRows, setKnownAssetsTotalRows ] = React.useState(0);
	const [ knownAssetsRows, setKnownAssetsRows ] = React.useState([]);

	const [ detectedAssetsTotalRows, setDetectedAssetsTotalRows ] = React.useState(0);
	const [ detectedAssetsRows, setDetectedAssetsRows ] = React.useState([]);

	const [ pageNumberKnownAssets, setPageNumberKnownAssets ] = React.useState(1);
	const [ pageNumberDetectedAssets, setPageNumberDetectedAssets ] = React.useState(1);
	const pageLimit = 24; // Instead of 24

	// State for the "Add Net Zone" feature
	const [ showAddNetZoneForm, setShowAddNetZoneForm ] = React.useState(false);
	const [ netZoneName, setNetZoneName ] = React.useState('');
	const [ firstIp, setFirstIp ] = React.useState('');
	const [ lastIp, setLastIp ] = React.useState('');
	const [ selectedCompanyForNetZone, setSelectedCompanyForNetZone ] = React.useState('');
	const [ availableCompanies, setAvailableCompanies ] = React.useState([]);

	// State for single asset entry
	const [ singleAssetIP, setSingleAssetIP ] = React.useState('');
	const [ singleAssetMAC, setSingleAssetMAC ] = React.useState('');
	const [ singleAssetVendor, setSingleAssetVendor ] = React.useState('');
	const [ singleAssetHostname, setSingleAssetHostname ] = React.useState('');
	const [ singleAssetZone, setSingleAssetZone ] = React.useState('');
	const [ singleAssetSwitchName, setSingleAssetSwitchName ] = React.useState('');
	const [ singleAssetSwitchPort, setSingleAssetSwitchPort ] = React.useState('');
	const [ assetEntryMode, setAssetEntryMode ] = React.useState('single'); // 'single' or 'file'
	const [ assetFile, setAssetFile ] = React.useState(null);

	// Add state for the IP traffic modal
	const [showIpTrafficModal, setShowIpTrafficModal] = React.useState(false);
	const [selectedIpAddress, setSelectedIpAddress] = React.useState('');

	// Add new state for managing selected detected assets
	const [ selectedDetectedAssets, setSelectedDetectedAssets ] = React.useState(new Set());
	const [ selectAllDetectedAssets, setSelectAllDetectedAssets ] = React.useState(false);
	const [ markKnownInProgress, setMarkKnownInProgress ] = React.useState(new Set());

		// Add these state variables around line 72-73, alongside the existing detected assets state:
		const [ selectedKnownAssets, setSelectedKnownAssets ] = React.useState(new Set());

	// Add this state alongside the existing state declarations (around line 31):
	const [ showEditKnownAssetsForm, setShowEditKnownAssetsForm ] = React.useState(false);
	const [ editingAssetData, setEditingAssetData ] = React.useState(null);
	const [ supportErrorMessage, setSupportErrorMessage ] = React.useState('');
	const [ copiedSupportError, setCopiedSupportError ] = React.useState(false);

	// Add these additional state variables for the edit form (around line 77, after editingAssetData):
	const [ editAssetIP, setEditAssetIP ] = React.useState('');
	const [ editAssetMAC, setEditAssetMAC ] = React.useState('');
	const [ editAssetVendor, setEditAssetVendor ] = React.useState('');
	const [ editAssetHostname, setEditAssetHostname ] = React.useState('');
	const [ editAssetOS, setEditAssetOS ] = React.useState('');
	const [ editAssetZone, setEditAssetZone ] = React.useState('');
	const [ editAssetSwitchName, setEditAssetSwitchName ] = React.useState('');
	const [ editAssetSwitchPort, setEditAssetSwitchPort ] = React.useState('');
	const [ editAssetAssignedPorts, setEditAssetAssignedPorts ] = React.useState('');
	const [ editAssetNote, setEditAssetNote ] = React.useState('');

	// Add the validatePorts function here:
	const validatePorts = (portsString) => {
		if (!portsString.trim()) return true; // Empty is valid
		
		const ports = portsString.split(',').map(p => p.trim());
		return ports.every(port => {
			const num = parseInt(port);
			return !isNaN(num) && num > 0 && num <= 65535 && port === num.toString();
		});
	};

	const [showNetworkTrafficModal, setShowNetworkTrafficModal] = React.useState(false);

	const setWatcherAssetsSupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
		setSupportErrorMessage(buildWatcherAssetsSupportError(errorCode, pageInfo, sourceMessage));
		setCopiedSupportError(false);
	}, []);

	const clearWatcherAssetsSupportError = React.useCallback((force = false) => {
		// Keep support errors visible until explicitly cleared by the user.
		if (!force) return;
		setSupportErrorMessage('');
		setCopiedSupportError(false);
	}, []);

	const handleCopySupportError = React.useCallback(async () => {
		if (!supportErrorMessage) return;
		try {
			await navigator.clipboard.writeText(supportErrorMessage);
			setCopiedSupportError(true);
			setTimeout(() => setCopiedSupportError(false), 2000);
		} catch (error) {
			setCopiedSupportError(false);
		}
	}, [ supportErrorMessage ]);

	React.useEffect(
		() => {
			if (
				userLogin &&
				userLogin.userInfo &&
				userLogin.userInfo.status === true &&
				userLogin.userInfo.session &&
				userLogin.userInfo.session.role
			) {
				setUserRole(userLogin.userInfo.session.role);
				setUserCompany(userLogin.userInfo.session.company || '');
			}
		},
		[ userLogin ]
	);

	// Fetch available companies for FLC users
	React.useEffect(() => {
		const fetchAvailableCompanies = async () => {
			if (userCompany === 'FLC') {
				try {
					const response = await fetch('/api/denied', {
						method: 'GET'
					});
					const data = await response.json();
					
					if (response.ok && data && data.availableCompanies && Array.isArray(data.availableCompanies)) {
						// Remove DEFAULT - only show actual companies
						setAvailableCompanies(data.availableCompanies);
						// Set first available company as default selection
						if (data.availableCompanies.length > 0) {
							setSelectedCompanyForNetZone(data.availableCompanies[0]);
						}
						clearWatcherAssetsSupportError();
					} else {
						setAvailableCompanies([]);
						setWatcherAssetsSupportError(
							FRONTEND_WATCHER_ASSETS_ERROR_CODES.COMPANY_LOAD_FAILED,
							'Watcher Assets Page / Available Companies Load Failed',
							data?.msg || `Denied list company query returned HTTP ${response.status}`
						);
					}
				} catch (error) {
					console.error('Error fetching available companies:', error);
					setAvailableCompanies([]);
					setWatcherAssetsSupportError(
						FRONTEND_WATCHER_ASSETS_ERROR_CODES.COMPANY_LOAD_FAILED,
						'Watcher Assets Page / Available Companies Request Error',
						error?.message || String(error)
					);
				}
			} else if (userCompany && userCompany !== 'None') {
				// For non-FLC users, they can only add to their own company
				setAvailableCompanies([userCompany]);
				setSelectedCompanyForNetZone(userCompany);
			}
		};

		if (userCompany) {
			fetchAvailableCompanies();
		}
	}, [ userCompany, clearWatcherAssetsSupportError, setWatcherAssetsSupportError ]);

	React.useEffect(
		() => {
			const getData = async () => {
				setDataLoading(true);
				clearWatcherAssetsSupportError();
				try {
					const data = await dispatch(getWatcherAssetsInitials());
					if (data && data.status) {
						setWatcherDataInitials(data);
						clearWatcherAssetsSupportError();
					} else {
						setWatcherDataInitials({});
						setWatcherAssetsSupportError(
							FRONTEND_WATCHER_ASSETS_ERROR_CODES.INITIAL_DATA_LOAD_FAILED,
							'Watcher Assets Page / Initial Data Load Failed',
							data?.msg || (typeof data === 'string' ? data : 'Watcher assets initials request did not return success')
						);
					}
				} catch (error) {
					setWatcherDataInitials({});
					setWatcherAssetsSupportError(
						FRONTEND_WATCHER_ASSETS_ERROR_CODES.INITIAL_DATA_LOAD_FAILED,
						'Watcher Assets Page / Initial Data Request Error',
						error?.message || String(error)
					);
				} finally {
					setDataLoading(false);
				}
			};

			getData();
		},
		[ dispatch, clearWatcherAssetsSupportError, setWatcherAssetsSupportError ]
	);

	React.useEffect(
		() => {
			let cancelled = false;
			const getData = async () => {
				if (!selectedWatcher) return;   // no data until the user picks a watcher
				setDataLoading(true);
				setWatcherData({});   // clear the previous watcher's rows immediately on switch
				const formData = new FormData();
				formData.append('watchers', selectedWatcher);
				clearWatcherAssetsSupportError();
				try {
					const data = await dispatch(getWatcherAssetsData(formData));
					if (cancelled) return;   // superseded by a newer selection

					if (data && data.status) {
						setWatcherData(data);
						clearWatcherAssetsSupportError();
					} else {
						setWatcherData({});
						setWatcherAssetsSupportError(
							FRONTEND_WATCHER_ASSETS_ERROR_CODES.WATCHER_DATA_LOAD_FAILED,
							'Watcher Assets Page / Watcher Data Load Failed',
							data?.msg || (typeof data === 'string' ? data : 'Watcher assets table request did not return success')
						);
					}
				} catch (error) {
					if (cancelled) return;
					setWatcherData({});
					setWatcherAssetsSupportError(
						FRONTEND_WATCHER_ASSETS_ERROR_CODES.WATCHER_DATA_LOAD_FAILED,
						'Watcher Assets Page / Watcher Data Request Error',
						error?.message || String(error)
					);
				} finally {
					if (!cancelled) {
						setDataLoading(false);
						setPageNumberKnownAssets(1);
						setPageNumberDetectedAssets(1);
					}
				}
			};

			getData();
			return () => { cancelled = true; };
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[ dispatch, selectedWatcher ]
	);

	// No auto-select: the page renders with the dropdown populated but no data
	// until the user picks a watcher. The data effect above early-returns while
	// selectedWatcher is empty.

	// Rows are arrays, so the sort field is the column index. Each table gets its
	// own useSort instance fed the searched set; we page-slice the sorted result
	// into the existing knownAssetsRows / detectedAssetsRows state below.
	const knownFilteredRows = React.useMemo(() => {
		const rows = (watcherData && Array.isArray(watcherData.KAssetsRows)) ? watcherData.KAssetsRows : [];
		if (!searchKnownAssetsKey) return rows;
		return rows.filter((row) =>
			row.find((el) => String(el ?? '').toLowerCase().includes(String(searchKnownAssetsKey ?? '').toLowerCase()))
		);
	}, [ watcherData, searchKnownAssetsKey ]);
	const knownSort = useSort(knownFilteredRows);

	const detectedFilteredRows = React.useMemo(() => {
		const rows = (watcherData && Array.isArray(watcherData.DAssetsRows)) ? watcherData.DAssetsRows : [];
		if (!searchDetectedAssetsKey) return rows;
		return rows.filter((row) =>
			row.find((el) => String(el ?? '').toLowerCase().includes(String(searchDetectedAssetsKey ?? '').toLowerCase()))
		);
	}, [ watcherData, searchDetectedAssetsKey ]);
	const detectedSort = useSort(detectedFilteredRows);

	// Known Assets: page-slice the searched + sorted rows.
	React.useEffect(
		() => {
			const sortedRows = Array.isArray(knownSort.sorted) ? knownSort.sorted : [];
			setKnownAssetsTotalRows(sortedRows.length);
			const startIndex = (pageNumberKnownAssets - 1) * pageLimit;
			setKnownAssetsRows(sortedRows.slice(startIndex, startIndex + pageLimit));
		},
		[ knownSort.sorted, pageNumberKnownAssets ]
	);

	// Detected Internal Assets: page-slice the searched + sorted rows.
	React.useEffect(
		() => {
			const sortedRows = Array.isArray(detectedSort.sorted) ? detectedSort.sorted : [];
			setDetectedAssetsTotalRows(sortedRows.length);
			const startIndex = (pageNumberDetectedAssets - 1) * pageLimit;
			setDetectedAssetsRows(sortedRows.slice(startIndex, startIndex + pageLimit));
		},
		[ detectedSort.sorted, pageNumberDetectedAssets ]
	);

	// A sort re-orders the whole set, so snap the affected table back to page 1
	// and drop any row-index selections (those are relative to the visible slice).
	React.useEffect(() => {
		setPageNumberKnownAssets(1);
		setSelectedKnownAssets(new Set());
	}, [ knownSort.sortKey, knownSort.sortDir ]);
	React.useEffect(() => {
		setPageNumberDetectedAssets(1);
		setSelectedDetectedAssets(new Set());
		setSelectAllDetectedAssets(false);
	}, [ detectedSort.sortKey, detectedSort.sortDir ]);

	// Fix the pagination handlers to work with ReactPaginate. The slice effects
	// above key off the page number, so the handlers just move the page.
	const handleDetectedAssetsPageChange = async (data) => {
		setPageNumberDetectedAssets(data.selected + 1);
		// Clear selections when changing pages
		setSelectedDetectedAssets(new Set());
		setSelectAllDetectedAssets(false);
	};

	const handleKnownAssetsPageChange = async (data) => {
		setPageNumberKnownAssets(data.selected + 1);
	};

		const handleAddKnownAssets = async () => {
		if (assetEntryMode === 'file') {
			// Handle file upload
			if (!assetFile) {
				toast.error('Please select a file to upload');
				return;
			}
			try {
				const formData = new FormData();
				formData.append('file', assetFile);

				const data = await dispatch(addKnownAssetsData(formData));
				if (data && data.status) {
					toast.success(data.msg);
					clearWatcherAssetsSupportError();
					// Reset form
					setAssetFile(null);
					setShowAddKnownAssetsForm(false);
					// Refresh data
					const refreshFormData = new FormData();
					refreshFormData.append('watchers', selectedWatcher);
					const refreshData = await dispatch(getWatcherAssetsData(refreshFormData));
					if (refreshData && refreshData.status) {
						setWatcherData(refreshData);
						clearWatcherAssetsSupportError();
					} else {
						const sourceMessage =
							refreshData?.msg ||
							(typeof refreshData === 'string'
								? refreshData
								: 'Watcher assets refresh after file upload did not return success');
						const formattedError = buildWatcherAssetsSupportError(
							FRONTEND_WATCHER_ASSETS_ERROR_CODES.WATCHER_DATA_LOAD_FAILED,
							'Watcher Assets Page / Refresh After File Upload Failed',
							sourceMessage
						);
						toast.error(formattedError);
						setWatcherAssetsSupportError(
							FRONTEND_WATCHER_ASSETS_ERROR_CODES.WATCHER_DATA_LOAD_FAILED,
							'Watcher Assets Page / Refresh After File Upload Failed',
							sourceMessage
						);
					}
				} else {
					const sourceMessage =
						data?.msg || (typeof data === 'string' ? data : 'Known assets file upload request did not return success');
					const formattedError = buildWatcherAssetsSupportError(
						FRONTEND_WATCHER_ASSETS_ERROR_CODES.ASSET_FILE_UPLOAD_FAILED,
						'Watcher Assets Page / Known Assets File Upload Failed',
						sourceMessage
					);
					toast.error(formattedError);
					setWatcherAssetsSupportError(
						FRONTEND_WATCHER_ASSETS_ERROR_CODES.ASSET_FILE_UPLOAD_FAILED,
						'Watcher Assets Page / Known Assets File Upload Failed',
						sourceMessage
					);
				}
			} catch (error) {
				const sourceMessage = error?.message || String(error);
				const formattedError = buildWatcherAssetsSupportError(
					FRONTEND_WATCHER_ASSETS_ERROR_CODES.ASSET_FILE_UPLOAD_FAILED,
					'Watcher Assets Page / Known Assets File Upload Request Error',
					sourceMessage
				);
				toast.error(formattedError);
				setWatcherAssetsSupportError(
					FRONTEND_WATCHER_ASSETS_ERROR_CODES.ASSET_FILE_UPLOAD_FAILED,
					'Watcher Assets Page / Known Assets File Upload Request Error',
					sourceMessage
				);
			}
		} else {
			// Handle single asset entry
			if (!singleAssetIP || !singleAssetMAC || !singleAssetVendor) {
				toast.error('Please fill in all required fields (IP, MAC, Vendor)');
				return;
			}
			try {
				// Create a CSV content for single asset - include WatcherID as first column
				const csvContent = `WatcherID,IP,MAC,Vendor,Hostname,FirstSeen,Known,Zone,SwitchName,SwitchPort\n${selectedWatcher},${singleAssetIP},${singleAssetMAC},${singleAssetVendor},${singleAssetHostname || ''},${new Date().toISOString()},1,${singleAssetZone || ''},${singleAssetSwitchName || ''},${singleAssetSwitchPort || ''}`;
				const blob = new Blob([csvContent], { type: 'text/csv' });
				const file = new File([blob], 'single_asset.csv', { type: 'text/csv' });

				const formData = new FormData();
				formData.append('file', file);

				const data = await dispatch(addKnownAssetsData(formData));
				if (data && data.status) {
					toast.success('Asset added successfully');
					clearWatcherAssetsSupportError();
					// Reset form
					setSingleAssetIP('');
					setSingleAssetMAC('');
					setSingleAssetVendor('');
					setSingleAssetHostname('');
					setSingleAssetZone('');
					setSingleAssetSwitchName('');
					setSingleAssetSwitchPort('');
					setShowAddKnownAssetsForm(false);
					// Refresh data
					const refreshFormData = new FormData();
					refreshFormData.append('watchers', selectedWatcher);
					const refreshData = await dispatch(getWatcherAssetsData(refreshFormData));
					if (refreshData && refreshData.status) {
						setWatcherData(refreshData);
						clearWatcherAssetsSupportError();
					} else {
						const sourceMessage =
							refreshData?.msg ||
							(typeof refreshData === 'string'
								? refreshData
								: 'Watcher assets refresh after single asset add did not return success');
						const formattedError = buildWatcherAssetsSupportError(
							FRONTEND_WATCHER_ASSETS_ERROR_CODES.WATCHER_DATA_LOAD_FAILED,
							'Watcher Assets Page / Refresh After Single Asset Add Failed',
							sourceMessage
						);
						toast.error(formattedError);
						setWatcherAssetsSupportError(
							FRONTEND_WATCHER_ASSETS_ERROR_CODES.WATCHER_DATA_LOAD_FAILED,
							'Watcher Assets Page / Refresh After Single Asset Add Failed',
							sourceMessage
						);
					}
				} else {
					const sourceMessage =
						data?.msg || (typeof data === 'string' ? data : 'Single asset add request did not return success');
					const formattedError = buildWatcherAssetsSupportError(
						FRONTEND_WATCHER_ASSETS_ERROR_CODES.ASSET_ADD_FAILED,
						'Watcher Assets Page / Add Single Asset Failed',
						sourceMessage
					);
					toast.error(formattedError);
					setWatcherAssetsSupportError(
						FRONTEND_WATCHER_ASSETS_ERROR_CODES.ASSET_ADD_FAILED,
						'Watcher Assets Page / Add Single Asset Failed',
						sourceMessage
					);
				}
			} catch (error) {
				const sourceMessage = error?.message || String(error);
				const formattedError = buildWatcherAssetsSupportError(
					FRONTEND_WATCHER_ASSETS_ERROR_CODES.ASSET_ADD_FAILED,
					'Watcher Assets Page / Add Single Asset Request Error',
					sourceMessage
				);
				toast.error(formattedError);
				setWatcherAssetsSupportError(
					FRONTEND_WATCHER_ASSETS_ERROR_CODES.ASSET_ADD_FAILED,
					'Watcher Assets Page / Add Single Asset Request Error',
					sourceMessage
				);
			}
		}
	};

	const handleAddNetZoneSubmit = async () => {
		if (!netZoneName || !firstIp || !lastIp) {
			toast.error('Please fill in all required fields');
			return;
		}

		try {
			const formData = new FormData();
			formData.append('netZoneName', netZoneName);
			formData.append('firstIp', firstIp);
			formData.append('lastIp', lastIp);
			formData.append('watcherId', selectedWatcher);
			
			// Add company selection for FLC users
			if (userCompany === 'FLC' && selectedCompanyForNetZone) {
				formData.append('company', selectedCompanyForNetZone);
			}

			const response = await fetch('/api/netzone/add', {
				method: 'POST',
				body: formData
			});

			const data = await response.json();
			
			if (data.status) {
				toast.success(data.msg || 'NetZone added successfully');
				clearWatcherAssetsSupportError();
				// Reset form
				setNetZoneName('');
				setFirstIp('');
				setLastIp('');
				setShowAddNetZoneForm(false);
				
				// Reset company selection for FLC users
				if (userCompany === 'FLC' && availableCompanies.length > 0) {
					setSelectedCompanyForNetZone(availableCompanies[0]);
				}
			} else {
				const sourceMessage =
					data?.msg || `NetZone add request returned HTTP ${response.status}`;
				const formattedError = buildWatcherAssetsSupportError(
					FRONTEND_WATCHER_ASSETS_ERROR_CODES.NETZONE_ADD_FAILED,
					'Watcher Assets Page / Add NetZone Failed',
					sourceMessage
				);
				toast.error(formattedError);
				setWatcherAssetsSupportError(
					FRONTEND_WATCHER_ASSETS_ERROR_CODES.NETZONE_ADD_FAILED,
					'Watcher Assets Page / Add NetZone Failed',
					sourceMessage
				);
			}
		} catch (error) {
			console.error('Error adding NetZone:', error);
			const sourceMessage = error?.message || String(error);
			const formattedError = buildWatcherAssetsSupportError(
				FRONTEND_WATCHER_ASSETS_ERROR_CODES.NETZONE_ADD_FAILED,
				'Watcher Assets Page / Add NetZone Request Error',
				sourceMessage
			);
			toast.error(formattedError);
			setWatcherAssetsSupportError(
				FRONTEND_WATCHER_ASSETS_ERROR_CODES.NETZONE_ADD_FAILED,
				'Watcher Assets Page / Add NetZone Request Error',
				sourceMessage
			);
		}
	};

	const exportToCSV = (tableData, columns, filename) => {
		try {
			// Create CSV content
			const csvContent = [
				// Header row
				columns.join(','),
				// Data rows
				...tableData.map(row => 
					row.map(cell => {
						// Handle cells that might contain commas or quotes
						const cellStr = String(cell || '');
						if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
							return `"${cellStr.replace(/"/g, '""')}"`;
						}
						return cellStr;
					}).join(',')
				)
			].join('\n');

			// Create and download file
			const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
			const link = document.createElement('a');
			const url = URL.createObjectURL(blob);
			link.setAttribute('href', url);
			link.setAttribute('download', `${filename}.csv`);
			link.style.visibility = 'hidden';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			
			toast.success(`Exported to ${filename}.csv`);
			clearWatcherAssetsSupportError();
		} catch (error) {
			console.error('CSV export error:', error);
			const sourceMessage = error?.message || String(error);
			const formattedError = buildWatcherAssetsSupportError(
				FRONTEND_WATCHER_ASSETS_ERROR_CODES.CSV_EXPORT_FAILED,
				'Watcher Assets Page / CSV Export Failed',
				sourceMessage
			);
			toast.error(formattedError);
			setWatcherAssetsSupportError(
				FRONTEND_WATCHER_ASSETS_ERROR_CODES.CSV_EXPORT_FAILED,
				'Watcher Assets Page / CSV Export Failed',
				sourceMessage
			);
		}
	};

	const exportToPDF = (tableData, columns, filename) => {
		if (!tableData || tableData.length === 0) {
			toast.error('No data to export');
			return;
		}

		try {
			console.log('PDF Export - Column names:', columns);
			
			const doc = new jsPDF('landscape'); // Use landscape for better column fit
			
			// Add title
			doc.setFontSize(16);
			doc.text(filename, 14, 15);
			
			// Include ALL columns
			const exportColumns = [...columns];
			const exportData = [...tableData];
			
			// Manually create a table with improved spacing
			const startY = 25;
			const cellPadding = 1; // Reduced padding
			const fontSize = 7;
			const baseRowHeight = 10; // Reduced row height
			const lineHeight = 6; // Reduced line height
			
			// Set font size for the table
			doc.setFontSize(fontSize);
			
			// Calculate column width (distribute more intelligently)
			const pageWidth = doc.internal.pageSize.width;
			const margin = 10;
			const tableWidth = pageWidth - 2 * margin;
			
			// Check if any column might contain long text (like hostnames, MACs, IPs)
			const hasLongTextColumn = columns.some(col => 
				col.toLowerCase().includes('hostname') || 
				col.toLowerCase().includes('mac') ||
				col.toLowerCase().includes('vendor') ||
				col.toLowerCase().includes('device')
			);
			
			let colWidths = [];
			if (hasLongTextColumn) {
				// Give more space to potentially long text columns with better balance
				colWidths = exportColumns.map((col, index) => {
					const colLower = col.toLowerCase();
					if (colLower.includes('first seen') || colLower.includes('firstseen')) {
						return tableWidth * 0.18; // 18% for First Seen column
					} else if (colLower.includes('hostname')) {
						return tableWidth * 0.20; // 20% for hostname (reduced from 25%)
					} else if (colLower.includes('vendor') || colLower.includes('device')) {
						return tableWidth * 0.18; // 18% for vendor/device columns
					} else if (colLower.includes('mac')) {
						return tableWidth * 0.17; // 17% for MAC addresses
					} else if (colLower.includes('zone')) {
						return tableWidth * 0.15; // 15% for zone column
					} else {
						return tableWidth * 0.12; // 12% for other columns (IP, Known, etc.)
					}
				});
				
				// Normalize to ensure total equals tableWidth
				const totalWidth = colWidths.reduce((sum, width) => sum + width, 0);
				colWidths = colWidths.map(width => (width / totalWidth) * tableWidth);
			} else {
				// Equal distribution for other tables
				const colWidth = tableWidth / exportColumns.length;
				colWidths = new Array(exportColumns.length).fill(colWidth);
			}
			
			// Helper function to split text into lines that fit within the given width
			const splitTextToLines = (text, maxWidth) => {
				const words = text.split(' ');
				let lines = [];
				let currentLine = '';
				
				words.forEach(word => {
					const testLine = currentLine + (currentLine ? ' ' : '') + word;
					if (doc.getTextWidth(testLine) <= maxWidth) {
						currentLine = testLine;
					} else {
						if (currentLine) lines.push(currentLine);
						currentLine = word;
					}
				});
				if (currentLine) lines.push(currentLine);
				
				return lines;
			};
			
			// Draw header
			doc.setFillColor(66, 139, 202); // Header background color
			doc.setTextColor(255, 255, 255); // Header text color
			doc.setFont(undefined, 'bold');
			
			doc.rect(margin, startY, tableWidth, baseRowHeight, 'F');
			
			let currentX = margin;
			exportColumns.forEach((column, i) => {
				doc.text(column.toString(), currentX + cellPadding, startY + baseRowHeight - cellPadding);
				currentX += colWidths[i];
			});
			
			// Draw rows
			doc.setTextColor(0, 0, 0); // Reset text color
			doc.setFont(undefined, 'normal');
			
			let y = startY + baseRowHeight;
			
			// Draw cells for each row
			exportData.forEach((row, rowIndex) => {
				// First pass: calculate the maximum number of lines needed for this row
				let maxLinesInRow = 1;
				const rowTextLines = []; // Store the lines for each cell
				
				row.forEach((cell, colIndex) => {
					if (cell !== null && cell !== undefined) {
						const text = cell.toString();
						const maxWidth = colWidths[colIndex] - (2 * cellPadding);
						const columnName = exportColumns[colIndex];
						
						// Check if this column might contain long text
						const colLower = columnName.toLowerCase();
						const isLongTextColumn = colLower.includes('hostname') || 
												colLower.includes('vendor') ||
												colLower.includes('device') ||
												colLower.includes('mac') ||
												text.length > 30;
						
						if (isLongTextColumn && text.length > 0) {
							const lines = splitTextToLines(text, maxWidth);
							const maxLines = 3; // Allow up to 3 lines for long text
							const finalLines = lines.length > maxLines ? 
								[...lines.slice(0, maxLines - 1), lines.slice(maxLines - 1).join(' ') + '...'] : 
								lines;
							
							rowTextLines[colIndex] = finalLines;
							maxLinesInRow = Math.max(maxLinesInRow, finalLines.length);
						} else {
							// For regular columns, check if text needs truncation
							if (doc.getTextWidth(text) <= maxWidth) {
								rowTextLines[colIndex] = [text];
							} else {
								let truncated = text;
								while (doc.getTextWidth(truncated + "...") > maxWidth && truncated.length > 0) {
									truncated = truncated.substring(0, truncated.length - 1);
								}
								rowTextLines[colIndex] = [truncated + "..."];
							}
						}
					} else {
						rowTextLines[colIndex] = [''];
					}
				});
				
				// Calculate row height based on maximum lines
				const rowHeight = Math.max(baseRowHeight, maxLinesInRow * lineHeight + (cellPadding * 2));
				
				// Set background color for alternating rows
				if (rowIndex % 2 === 0) {
					doc.setFillColor(240, 240, 240);
				} else {
					doc.setFillColor(255, 255, 255);
				}
				
				doc.rect(margin, y, tableWidth, rowHeight, 'F');
				
				// Second pass: draw the text lines
				currentX = margin;
				row.forEach((cell, colIndex) => {
					const lines = rowTextLines[colIndex];
					
					// Draw each line of text for this cell
					lines.forEach((line, lineIndex) => {
						const textY = y + cellPadding + (lineIndex + 1) * lineHeight - 1;
						doc.text(
							line,
							currentX + cellPadding,
							textY
						);
					});
					
					currentX += colWidths[colIndex];
				});
				
				y += rowHeight;
				
				// Add new page if needed
				if (y > doc.internal.pageSize.height - 20) {
					doc.addPage();
					y = 25;
				}
			});
			
			// Save PDF
			doc.save(`${filename}.pdf`);
			
			toast.success(`Exported to ${filename}.pdf`);
			clearWatcherAssetsSupportError();
		} catch (error) {
			console.error('PDF generation error:', error);
			const sourceMessage = error?.message || String(error);
			const formattedError = buildWatcherAssetsSupportError(
				FRONTEND_WATCHER_ASSETS_ERROR_CODES.PDF_EXPORT_FAILED,
				'Watcher Assets Page / PDF Export Failed',
				sourceMessage
			);
			toast.error(formattedError);
			setWatcherAssetsSupportError(
				FRONTEND_WATCHER_ASSETS_ERROR_CODES.PDF_EXPORT_FAILED,
				'Watcher Assets Page / PDF Export Failed',
				sourceMessage
			);
		}
	};

	// Add function to handle IP click
	const handleIpClick = (ipAddress) => {
		setSelectedIpAddress(ipAddress);
		setShowIpTrafficModal(true);
	};

	// Add function to close IP traffic modal
	const closeIpTrafficModal = () => {
		setShowIpTrafficModal(false);
		setSelectedIpAddress('');
	};

	// Add functions for handling asset selection
	const handleSelectDetectedAsset = (rowIndex, isChecked) => {
		const newSelectedAssets = new Set(selectedDetectedAssets);
		
		if (isChecked) {
			newSelectedAssets.add(rowIndex);
		} else {
			newSelectedAssets.delete(rowIndex);
			setSelectAllDetectedAssets(false);
		}
		
		setSelectedDetectedAssets(newSelectedAssets);
	};

	const handleSelectAllDetectedAssets = (isChecked) => {
		if (isChecked) {
			const allRowIndexes = new Set();
			detectedAssetsRows.forEach((_, index) => allRowIndexes.add(index));
			setSelectedDetectedAssets(allRowIndexes);
		} else {
			setSelectedDetectedAssets(new Set());
		}
		setSelectAllDetectedAssets(isChecked);
	};

	const mapDetectedAssetRow = (row = []) => ({
		watcherName: row[0],
		deviceIP: row[1],
		hostname: row[2],
		deviceMAC: row[3],
		deviceVendor: row[4],
		zone: row[5],
		firstSeen: row[7],
	});

	const csvEscapeValue = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

	const createKnownAssetsCsvContent = (assets = []) => {
		const header = 'WatcherID,IP,MAC,Vendor,Hostname,FirstSeen,Known,Zone,SwitchName,SwitchPort';
		const rows = assets.map((asset) =>
			[
				selectedWatcher,
				asset.deviceIP,
				asset.deviceMAC,
				asset.deviceVendor,
				asset.hostname || '',
				asset.firstSeen || '',
				'1',
				asset.zone || '',
				'',
				'',
			]
				.map(csvEscapeValue)
				.join(',')
		);
		return `${header}\n${rows.join('\n')}${rows.length ? '\n' : ''}`;
	};

	// Add function to move selected detected assets to known assets
	const handleMoveToKnownAssets = async () => {
		if (selectedDetectedAssets.size === 0) {
			toast.error('Please select assets to move');
			return;
		}

		try {
			const selectedAssetData = Array.from(selectedDetectedAssets)
				.map((rowIndex) => mapDetectedAssetRow(detectedAssetsRows[rowIndex]))
				.filter((asset) => asset.deviceIP && asset.deviceMAC);
			if (selectedAssetData.length === 0) {
				toast.error('Selected assets are missing IP or MAC values');
				return;
			}

			const csvContent = createKnownAssetsCsvContent(selectedAssetData);

			const blob = new Blob([csvContent], { type: 'text/csv' });
			const file = new File([blob], 'bulk_detected_assets.csv', { type: 'text/csv' });

			const formData = new FormData();
			formData.append('file', file);

			const data = await dispatch(addKnownAssetsData(formData));
			if (data && data.status) {
				toast.success(`Successfully moved ${selectedDetectedAssets.size} asset(s) to Known Assets`);
				clearWatcherAssetsSupportError();
				
				// Clear selections
				setSelectedDetectedAssets(new Set());
				setSelectAllDetectedAssets(false);
				
				// Refresh data
				const refreshFormData = new FormData();
				refreshFormData.append('watchers', selectedWatcher);
				const refreshData = await dispatch(getWatcherAssetsData(refreshFormData));
				if (refreshData && refreshData.status) {
					setWatcherData(refreshData);
					clearWatcherAssetsSupportError();
				} else {
					const sourceMessage =
						refreshData?.msg ||
						(typeof refreshData === 'string'
							? refreshData
							: 'Watcher assets refresh after moving detected assets did not return success');
					const formattedError = buildWatcherAssetsSupportError(
						FRONTEND_WATCHER_ASSETS_ERROR_CODES.WATCHER_DATA_LOAD_FAILED,
						'Watcher Assets Page / Refresh After Move To Known Failed',
						sourceMessage
					);
					toast.error(formattedError);
					setWatcherAssetsSupportError(
						FRONTEND_WATCHER_ASSETS_ERROR_CODES.WATCHER_DATA_LOAD_FAILED,
						'Watcher Assets Page / Refresh After Move To Known Failed',
						sourceMessage
					);
				}
			} else {
				const sourceMessage =
					data?.msg || (typeof data === 'string' ? data : 'Move detected assets request did not return success');
				const formattedError = buildWatcherAssetsSupportError(
					FRONTEND_WATCHER_ASSETS_ERROR_CODES.MOVE_ASSETS_FAILED,
					'Watcher Assets Page / Move To Known Assets Failed',
					sourceMessage
				);
				toast.error(formattedError);
				setWatcherAssetsSupportError(
					FRONTEND_WATCHER_ASSETS_ERROR_CODES.MOVE_ASSETS_FAILED,
					'Watcher Assets Page / Move To Known Assets Failed',
					sourceMessage
				);
			}
		} catch (error) {
			console.error('Error moving assets:', error);
			const sourceMessage = error?.message || String(error);
			const formattedError = buildWatcherAssetsSupportError(
				FRONTEND_WATCHER_ASSETS_ERROR_CODES.MOVE_ASSETS_FAILED,
				'Watcher Assets Page / Move To Known Assets Request Error',
				sourceMessage
			);
			toast.error(formattedError);
			setWatcherAssetsSupportError(
				FRONTEND_WATCHER_ASSETS_ERROR_CODES.MOVE_ASSETS_FAILED,
				'Watcher Assets Page / Move To Known Assets Request Error',
				sourceMessage
			);
		}
	};

	const handleMarkDetectedAssetKnown = async (rowData) => {
		const asset = mapDetectedAssetRow(rowData);
		if (!asset.deviceIP || !asset.deviceMAC) {
			toast.error('Asset is missing IP or MAC and cannot be marked as known');
			return;
		}

		const assetKey = `${selectedWatcher}:${asset.deviceIP}:${asset.deviceMAC}`;
		if (markKnownInProgress.has(assetKey)) return;

		setMarkKnownInProgress((prev) => {
			const next = new Set(prev);
			next.add(assetKey);
			return next;
		});

		try {
			const csvContent = createKnownAssetsCsvContent([asset]);
			const blob = new Blob([csvContent], { type: 'text/csv' });
			const file = new File([blob], 'single_detected_asset.csv', { type: 'text/csv' });

			const formData = new FormData();
			formData.append('file', file);

			const data = await dispatch(addKnownAssetsData(formData));
			if (data && data.status) {
				toast.success(`Marked ${asset.deviceIP} as known`);
				clearWatcherAssetsSupportError();

				const refreshFormData = new FormData();
				refreshFormData.append('watchers', selectedWatcher);
				const refreshData = await dispatch(getWatcherAssetsData(refreshFormData));
				if (refreshData && refreshData.status) {
					setWatcherData(refreshData);
					setSelectedDetectedAssets(new Set());
					setSelectAllDetectedAssets(false);
					clearWatcherAssetsSupportError();
				} else {
					const sourceMessage =
						refreshData?.msg ||
						(typeof refreshData === 'string'
							? refreshData
							: 'Watcher assets refresh after marking asset known did not return success');
					const formattedError = buildWatcherAssetsSupportError(
						FRONTEND_WATCHER_ASSETS_ERROR_CODES.WATCHER_DATA_LOAD_FAILED,
						'Watcher Assets Page / Refresh After Mark Known Failed',
						sourceMessage
					);
					toast.error(formattedError);
					setWatcherAssetsSupportError(
						FRONTEND_WATCHER_ASSETS_ERROR_CODES.WATCHER_DATA_LOAD_FAILED,
						'Watcher Assets Page / Refresh After Mark Known Failed',
						sourceMessage
					);
				}
			} else {
				const sourceMessage =
					data?.msg || (typeof data === 'string' ? data : 'Mark asset known request did not return success');
				const formattedError = buildWatcherAssetsSupportError(
					FRONTEND_WATCHER_ASSETS_ERROR_CODES.MOVE_ASSETS_FAILED,
					'Watcher Assets Page / Mark Asset Known Failed',
					sourceMessage
				);
				toast.error(formattedError);
				setWatcherAssetsSupportError(
					FRONTEND_WATCHER_ASSETS_ERROR_CODES.MOVE_ASSETS_FAILED,
					'Watcher Assets Page / Mark Asset Known Failed',
					sourceMessage
				);
			}
		} catch (error) {
			const sourceMessage = error?.message || String(error);
			const formattedError = buildWatcherAssetsSupportError(
				FRONTEND_WATCHER_ASSETS_ERROR_CODES.MOVE_ASSETS_FAILED,
				'Watcher Assets Page / Mark Asset Known Request Error',
				sourceMessage
			);
			toast.error(formattedError);
			setWatcherAssetsSupportError(
				FRONTEND_WATCHER_ASSETS_ERROR_CODES.MOVE_ASSETS_FAILED,
				'Watcher Assets Page / Mark Asset Known Request Error',
				sourceMessage
			);
		} finally {
			setMarkKnownInProgress((prev) => {
				const next = new Set(prev);
				next.delete(assetKey);
				return next;
			});
		}
	};

	// Helper function to render cell content with clickable IPs and styling
	const renderCellContent = (cell, cellIndex, cols, rowData = null, isDetectedAssetsTable = false) => {
		// cols comes from the initials fetch while the rows come from the data
		// fetch; when the data resolves first, rows render before cols exist, so
		// guard rather than crash the whole page (reading a property of undefined).
		const columnName = cols ? cols[cellIndex] : undefined;
		const isIpColumn = columnName && (
			columnName.toLowerCase().includes('ip') || 
			columnName.toLowerCase().includes('device ip')
		);

		// Check if this column contains MAC addresses
		const isMacColumn = columnName && columnName.toLowerCase().includes('mac');

		// Check if this column contains First seen data
		const isFirstSeenColumn = columnName && (
			columnName.toLowerCase().includes('first seen') || 
			columnName.toLowerCase().includes('firstseen')
		);

		// Check if this is the Known column in detected assets table
		const isKnownColumn = columnName && columnName.toLowerCase().includes('known');

		// Check if this column contains vendor/device information
		const isVendorColumn = columnName && (
			columnName.toLowerCase().includes('vendor') || 
			columnName.toLowerCase().includes('device')
		);

		// Simple IP address validation regex
		const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
		
		// Simple MAC address validation regex (supports various formats)
		const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;

		if (isIpColumn && cell && ipRegex.test(cell.toString())) {
			return (
				<span 
					className={WatcherAssetsStyles.clickable_ip}
					onClick={() => handleIpClick(cell.toString())}
					title="Click to view traffic details"
				>
					{cell}
				</span>
			);
		}

		if (isMacColumn && cell && macRegex.test(cell.toString())) {
			return (
				<span className={WatcherAssetsStyles.styled_mac}>
					{cell}
				</span>
			);
		}

		if (isFirstSeenColumn && cell) {
			return (
				<span className={WatcherAssetsStyles.styled_first_seen}>
					{cell}
				</span>
			);
		}

		// Handle Known column styling in detected assets table
		if (isDetectedAssetsTable && isKnownColumn && cell !== null && cell !== undefined) {
			const isKnown = cell.toString() === '1' || cell.toString().toLowerCase() === 'true' || cell.toString().toLowerCase() === 'yes';
			return (
				<span className={isKnown ? WatcherAssetsStyles.known_status : WatcherAssetsStyles.unknown_status}>
					{cell}
				</span>
			);
		}

		// Handle Vendor/Device column styling
		if (isVendorColumn && cell) {
			return (
				<span className={WatcherAssetsStyles.styled_vendor}>
					{cell}
				</span>
			);
		}

		return cell;
	};

	// Helper function to get row class based on known status
	const getRowClass = (rowData, cols, isDetectedAssetsTable = false) => {
		if (!isDetectedAssetsTable) return '';

		const knownColumnIndex = (cols || []).findIndex(col => col.toLowerCase().includes('known'));
		if (knownColumnIndex === -1) return '';
		
		const knownValue = rowData[knownColumnIndex];
		const isKnown = knownValue && (knownValue.toString() === '1' || knownValue.toString().toLowerCase() === 'true' || knownValue.toString().toLowerCase() === 'yes');
		
		return isKnown ? WatcherAssetsStyles.known_asset : WatcherAssetsStyles.unknown_asset;
	};

	// Add this function to get the current watcher name
	const getCurrentWatcherName = () => {
		if (!selectedWatcher) return "Watcher";
		
		if (watcherDataInitials && watcherDataInitials.watchers && watcherDataInitials.watchers.length > 0) {
			const currentWatcher = watcherDataInitials.watchers.find(watcher => watcher[0] === selectedWatcher);
			return currentWatcher ? currentWatcher[1] : "Watcher";
		}
		
		return "Watcher";
	};

	// Add this function to generate dynamic filenames
	const generateFilename = (dataType) => {
		const watcherName = getCurrentWatcherName();
		return `${watcherName} ${dataType}`;
	};

	// Add a handler for search key changes that resets pagination
	const handleKnownAssetsSearchChange = (e) => {
		setSearchKnownAssetsKey(e.target.value);
		setPageNumberKnownAssets(1); // Reset to first page when searching
	};

	const handleDetectedAssetsSearchChange = (e) => {
		setSearchDetectedAssetsKey(e.target.value);
		setPageNumberDetectedAssets(1); // Reset to first page when searching
	};

	// Add this handler alongside the existing handlers (around line 700):
	const handleEditKnownAsset = (assetData, rowIndex) => {
		setEditingAssetData({ data: assetData, index: rowIndex });
		
		// Pre-populate the form fields with existing data
		if (assetData && watcherDataInitials.KAssetsCols) {
			const cols = watcherDataInitials.KAssetsCols;

			// Debug: Log the data structure to console
			console.log('Asset Data:', assetData);
			console.log('Columns:', cols);
			console.log('Asset Data Length:', assetData.length);
			console.log('Columns Length:', cols.length);

			// Map the data to the form fields based on column names
			setEditAssetIP(assetData[cols.findIndex(col => col.toLowerCase().includes('ip'))] || '');
			setEditAssetMAC(assetData[cols.findIndex(col => col.toLowerCase().includes('mac'))] || '');
			setEditAssetVendor(assetData[cols.findIndex(col => col.toLowerCase().includes('vendor'))] || '');
			setEditAssetHostname(assetData[cols.findIndex(col => col.toLowerCase().includes('hostname'))] || '');
			// Match the OS column exactly — a loose includes('os') also matches
			// "Hostname" (h-os-tname), which put the hostname in the Device OS field.
			setEditAssetOS(assetData[cols.findIndex(col => { const c = col.toLowerCase(); return c === 'device os' || c === 'os'; })] || '');
			setEditAssetZone(assetData[cols.findIndex(col => col.toLowerCase().includes('zone'))] || '');
			const switchInfoIndex = cols.findIndex(col => col.toLowerCase().includes('switch') && col.toLowerCase().includes('info'));
			if (switchInfoIndex !== -1 && assetData[switchInfoIndex]) {
				try {
					const switchInfo = JSON.parse(assetData[switchInfoIndex]);
					setEditAssetSwitchName(switchInfo.switch_name || '');
					setEditAssetSwitchPort(switchInfo.switch_port || '');
				} catch (e) {
					setEditAssetSwitchName('');
					setEditAssetSwitchPort('');
				}
			} else {
				setEditAssetSwitchName('');
				setEditAssetSwitchPort('');
			}
			
			// Device Services (assigned ports) - based on the backend, this should be at index 7
			const assignedPorts = assetData[7] || '';
			setEditAssetAssignedPorts(assignedPorts);
			
			// Note - based on the backend, this should be at index 8 (the last element)
			const noteValue = assetData[8] || '';
			console.log('Note value at index 8:', noteValue);
			setEditAssetNote(noteValue);
		}
		
		setShowEditKnownAssetsForm(true);
	};

	// Replace the current handleEditKnownAssets function (around line 902):
	const handleEditKnownAssets = async () => {
		try {
			if (!editingAssetData || !editingAssetData.data) {
				toast.error('No asset data to update');
				return;
			}

			// Get the original asset data to find the unique identifiers
			const originalData = editingAssetData.data;
			const cols = watcherDataInitials.KAssetsCols;
			
			// Find the indices for required fields
			const ipIndex = cols.findIndex(col => col.toLowerCase().includes('ip'));
			const macIndex = cols.findIndex(col => col.toLowerCase().includes('mac'));
			
			if (ipIndex === -1 || macIndex === -1) {
				toast.error('Cannot identify asset to update');
				return;
			}

			// Validate ports before submitting
			if (editAssetAssignedPorts && !validatePorts(editAssetAssignedPorts)) {
				toast.error('Please enter valid port numbers separated by commas');
				return;
			}

			// Clean up the ports string (remove extra spaces, normalize commas)
			const cleanedPorts = editAssetAssignedPorts
				.split(',')
				.map(p => p.trim())
				.filter(p => p)
				.join(',');

            // Prepare the update data
            const updateData = {
                device_ip: originalData[ipIndex],
                device_mac: originalData[macIndex],
                watcher_id: selectedWatcher,
                hostname: editAssetHostname,
                vendor: editAssetVendor,
                device_os: editAssetOS,
                zone: editAssetZone,
                device_services: cleanedPorts,
                switch_name: editAssetSwitchName,
                switch_port: editAssetSwitchPort,
                note: editAssetNote,
            };			
			
			const response = await fetch('/api/assets/update', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(updateData)
			});

			const result = await response.json();

			if (result.status) {
				toast.success('Asset updated successfully');
				clearWatcherAssetsSupportError();
				
				// Clear the form and close it
					setShowEditKnownAssetsForm(false);
					setEditingAssetData(null);
					setSelectedKnownAssets(new Set());
					setEditAssetIP('');
				setEditAssetMAC('');
				setEditAssetVendor('');
				setEditAssetHostname('');
				setEditAssetOS('');
				setEditAssetZone('');
				setEditAssetSwitchName('');
				setEditAssetSwitchPort('');
				setEditAssetAssignedPorts('');
				setEditAssetNote('');
				
				// Refresh data to show the updated asset
				const formData = new FormData();
				formData.append('watchers', selectedWatcher);
				const refreshData = await dispatch(getWatcherAssetsData(formData));
				if (refreshData && refreshData.status) {
					setWatcherData(refreshData);
					clearWatcherAssetsSupportError();
				} else {
					const sourceMessage =
						refreshData?.msg ||
						(typeof refreshData === 'string'
							? refreshData
							: 'Watcher assets refresh after updating asset did not return success');
					const formattedError = buildWatcherAssetsSupportError(
						FRONTEND_WATCHER_ASSETS_ERROR_CODES.WATCHER_DATA_LOAD_FAILED,
						'Watcher Assets Page / Refresh After Asset Update Failed',
						sourceMessage
					);
					toast.error(formattedError);
					setWatcherAssetsSupportError(
						FRONTEND_WATCHER_ASSETS_ERROR_CODES.WATCHER_DATA_LOAD_FAILED,
						'Watcher Assets Page / Refresh After Asset Update Failed',
						sourceMessage
					);
				}
				
			} else {
				const sourceMessage =
					result?.msg || (typeof result === 'string' ? result : 'Asset update request did not return success');
				const formattedError = buildWatcherAssetsSupportError(
					FRONTEND_WATCHER_ASSETS_ERROR_CODES.ASSET_UPDATE_FAILED,
					'Watcher Assets Page / Update Asset Failed',
					sourceMessage
				);
				toast.error(formattedError);
				setWatcherAssetsSupportError(
					FRONTEND_WATCHER_ASSETS_ERROR_CODES.ASSET_UPDATE_FAILED,
					'Watcher Assets Page / Update Asset Failed',
					sourceMessage
				);
			}
			
		} catch (error) {
			console.error('Error updating asset:', error);
			const sourceMessage = error?.message || String(error);
			const formattedError = buildWatcherAssetsSupportError(
				FRONTEND_WATCHER_ASSETS_ERROR_CODES.ASSET_UPDATE_FAILED,
				'Watcher Assets Page / Update Asset Request Error',
				sourceMessage
			);
			toast.error(formattedError);
			setWatcherAssetsSupportError(
				FRONTEND_WATCHER_ASSETS_ERROR_CODES.ASSET_UPDATE_FAILED,
				'Watcher Assets Page / Update Asset Request Error',
				sourceMessage
			);
		}
	};

	const handleSelectKnownAsset = (rowIndex, isChecked) => {
		const newSelectedAssets = new Set(selectedKnownAssets);
		
		if (isChecked) {
			newSelectedAssets.add(rowIndex);
		} else {
			newSelectedAssets.delete(rowIndex);
		}
		
			setSelectedKnownAssets(newSelectedAssets);
		};

	const openNetworkTrafficMap = () => {
		if (!selectedWatcher || !watcherData || Object.keys(watcherData).length === 0) {
			toast.error('Please select a watcher with data to view the network traffic map');
			return;
		}
		setShowNetworkTrafficModal(true);
	};

	const closeNetworkTrafficMap = () => {
		setShowNetworkTrafficModal(false);
	};

	return (
		<Content
			currentMenu='watchers'
			headerTitle='Watcher Assets'
			selectBox={true}
			selectBoxValue={selectedWatcher}
			selectOnchange={(e) => setSelectedWatcher(e.target.value)}
			options={
				watcherDataInitials &&
				Object.keys(watcherDataInitials).length === 0 &&
				watcherDataInitials.watchers &&
				watcherDataInitials.watchers.length === 0 ? (
					<option value=''>No watchers found</option>
				) : (
					<>
						<option value=''>Select a Watcher</option>
						<option value='All Watchers'>All Watchers</option>
						{watcherDataInitials &&
							watcherDataInitials.watchers &&
							watcherDataInitials.watchers.length > 0 &&
							watcherDataInitials.watchers.map((item, index) => (
								<option key={index} value={item[0]}>
									{item[1]}
								</option>
							))}
					</>
				)
			}>
			{/* Add the IP Traffic Modal */}
			<IpTrafficModal
				isOpen={showIpTrafficModal}
				onClose={closeIpTrafficModal}
				ipAddress={selectedIpAddress}
				watcherId={selectedWatcher}
			/>
			{/* Add the Network Traffic Modal */}
			<NetworkTrafficModal
				isOpen={showNetworkTrafficModal}
				onClose={closeNetworkTrafficMap}
				watcherId={selectedWatcher}
				watcherName={getCurrentWatcherName()}
			/>
			{dataLoading ? (
				<Loader />
			) : !selectedWatcher ? (
				<div style={{ padding: '4rem 2rem', textAlign: 'center', color: 'var(--text-secondary, #6d6d6d)', fontSize: '1.4rem', fontWeight: 600 }}>
					Select a watcher from the menu above to view its assets.
				</div>
			) : watcherData && Object.keys(watcherData).length === 0 ? (
				<div>
					<NetworkError />
						{supportErrorMessage && (
							<div className={WatcherAssetsStyles.supportErrorCard}>
								<strong>Support Error:</strong>
								<code className={WatcherAssetsStyles.supportErrorCode}>{supportErrorMessage}</code>
								<div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
									<button
										type='button'
										className={WatcherAssetsStyles.copySupportErrorButton}
										onClick={handleCopySupportError}
									>
										{copiedSupportError ? 'Copied' : 'Copy Error Details'}
									</button>
									<button
										type='button'
										className={WatcherAssetsStyles.copySupportErrorButton}
										onClick={() => clearWatcherAssetsSupportError(true)}
									>
										Clear
									</button>
								</div>
							</div>
						)}
				</div>
			) : (
				<div className={WatcherAssetsStyles.tables}>
						{supportErrorMessage && (
							<div className={WatcherAssetsStyles.supportErrorBanner}>
								<div className={WatcherAssetsStyles.supportErrorBannerText}>
									<strong>Support Error:</strong>
									<code className={WatcherAssetsStyles.supportErrorCode}>{supportErrorMessage}</code>
								</div>
								<div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
									<button
										type='button'
										className={WatcherAssetsStyles.copySupportErrorButton}
										onClick={handleCopySupportError}
									>
										{copiedSupportError ? 'Copied' : 'Copy Error Details'}
									</button>
									<button
										type='button'
										className={WatcherAssetsStyles.copySupportErrorButton}
										onClick={() => clearWatcherAssetsSupportError(true)}
									>
										Clear
									</button>
								</div>
							</div>
						)}
					<div className={WatcherAssetsStyles.table}>
						<div className={WatcherAssetsStyles.table_heading}>
							<div className={WatcherAssetsStyles.table_header}>
								<h3 className={WatcherAssetsStyles.table_header_text}>Known Assets</h3>
								
								{userRole === 'admin' && (
									<>
										<button
											className={WatcherAssetsStyles.export_button}
											onClick={() => setShowAddKnownAssetsForm(prev => !prev)}
											data-testid='add-known-assets'>
											{showAddKnownAssetsForm ? 'Cancel' : 'Add Known Assets'}
										</button>
										{userRole === 'admin' && selectedKnownAssets.size > 0 && (
											<button
												className={WatcherAssetsStyles.export_button}
												onClick={() => {
													// If only one asset is selected, pre-populate the form
													if (selectedKnownAssets.size === 1) {
														const selectedIndex = Array.from(selectedKnownAssets)[0];
														const assetData = knownAssetsRows[selectedIndex];
														handleEditKnownAsset(assetData, selectedIndex);
													} else {
														// Multiple assets selected - could show bulk edit or just show form
														setShowEditKnownAssetsForm(true);
													}
												}}
												data-testid='edit-known-assets'>
												Edit Selected Assets ({selectedKnownAssets.size})
											</button>
										)}
									</>
								)}
								
								<div className={WatcherAssetsStyles.export_buttons}>
									<button
										className={WatcherAssetsStyles.export_button}
										onClick={() => {
											const rows = (watcherData && Array.isArray(watcherData.KAssetsRows)) ? watcherData.KAssetsRows : [];
											const cols = (watcherData && Array.isArray(watcherData.KAssetsCols)) ? watcherData.KAssetsCols : [];
											const colCount = cols.length;

											const filteredRows = rows.filter((row) =>
												searchKnownAssetsKey
													? row && row.find((el) => String(el ?? '').toLowerCase().includes(String(searchKnownAssetsKey ?? '').toLowerCase()))
													: true
											);

											const trimmedRows = colCount
												? filteredRows.map(r => Array.isArray(r) ? r.slice(0, colCount) : r)
												: filteredRows;

											exportToCSV(trimmedRows, cols, generateFilename('Known Assets'));
										}}
									>
										Export CSV
									</button>
									<button
										className={WatcherAssetsStyles.export_button}
										onClick={() => {
											const rows = (watcherData && Array.isArray(watcherData.KAssetsRows)) ? watcherData.KAssetsRows : [];
											const cols = (watcherData && Array.isArray(watcherData.KAssetsCols)) ? watcherData.KAssetsCols : [];
											const colCount = cols.length;

											const filteredRows = rows.filter((row) =>
												searchKnownAssetsKey
													? row && row.find((el) => String(el ?? '').toLowerCase().includes(String(searchKnownAssetsKey ?? '').toLowerCase()))
													: true
											);

											const trimmedRows = colCount
												? filteredRows.map(r => Array.isArray(r) ? r.slice(0, colCount) : r)
												: filteredRows;

											exportToPDF(trimmedRows, cols, generateFilename('Known Assets'));
										}}
									>
										Export PDF
									</button>
									<button
										className={WatcherAssetsStyles.export_button}
										onClick={openNetworkTrafficMap}
										disabled={!selectedWatcher || !watcherData || Object.keys(watcherData).length === 0}
										data-testid='network-traffic-button'>
										Network Traffic Map
									</button>
								</div>
							</div>
							<TextInput
								icon='search'
								placeholder='Search Here'
								value={searchKnownAssetsKey}
								onChange={handleKnownAssetsSearchChange}
								style={{ width: '15rem' }}
							/>
						</div>

						{showAddKnownAssetsForm && userRole === 'admin' && (
							<div className={WatcherAssetsStyles.add_net_zone_form_container}>
								{/* Mode selection */}
								<div>
									<label>Import Method</label>
									<div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
										<label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 'normal' }}>
											<input
												type="radio"
												name="assetEntryMode"
												value="single"
												checked={assetEntryMode === 'single'}
												onChange={(e) => setAssetEntryMode(e.target.value)}
											/>
											Single Asset
										</label>
										<label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 'normal' }}>
											<input
												type="radio"
												name="assetEntryMode"
												value="file"
												checked={assetEntryMode === 'file'}
												onChange={(e) => setAssetEntryMode(e.target.value)}
											/>
											CSV File Upload
										</label>
									</div>
								</div>

								{assetEntryMode === 'single' ? (
									<>
										<div>
											<label htmlFor="singleAssetIP">IP Address *</label>
											<TextInput
												id="singleAssetIP"
												placeholder="Enter IP Address"
												value={singleAssetIP}
												onChange={(e) => setSingleAssetIP(e.target.value)}
											/>
										</div>
										<div>
											<label htmlFor="singleAssetMAC">MAC Address *</label>
											<TextInput
												id="singleAssetMAC"
												placeholder="Enter MAC Address"
												value={singleAssetMAC}
												onChange={(e) => setSingleAssetMAC(e.target.value)}
											/>
										</div>
										<div>
											<label htmlFor="singleAssetVendor">Vendor *</label>
											<TextInput
												id="singleAssetVendor"
												placeholder="Enter Vendor"
												value={singleAssetVendor}
												onChange={(e) => setSingleAssetVendor(e.target.value)}
											/>
										</div>
										<div>
											<label htmlFor="singleAssetHostname">Hostname</label>
											<TextInput
												id="singleAssetHostname"
												placeholder="Enter Hostname "
												value={singleAssetHostname}
												onChange={(e) => setSingleAssetHostname(e.target.value)}
											/>
										</div>
										<div>
											<label htmlFor="singleAssetZone">Zone</label>
											<TextInput
												id="singleAssetZone"
												placeholder="Enter Zone "
												value={singleAssetZone}
												onChange={(e) => setSingleAssetZone(e.target.value)}
											/>
										</div>
										<div>
											<label htmlFor="singleAssetSwitchName">Switch Name</label>
											<TextInput
												id="singleAssetSwitchName"
												placeholder="Enter Switch Name"
												value={singleAssetSwitchName}
												onChange={(e) => setSingleAssetSwitchName(e.target.value)}
											/>
										</div>
										<div>
											<label htmlFor="singleAssetSwitchPort">Switch Port</label>
											<TextInput
												id="singleAssetSwitchPort"
												placeholder="Enter Switch Port"
												value={singleAssetSwitchPort}
												onChange={(e) => setSingleAssetSwitchPort(e.target.value)}
											/>
										</div>
									</>
								) : (
									<div>
										<label htmlFor="assetFileInput">Choose CSV File *</label>
										<input
											id="assetFileInput"
											type="file"
											accept=".csv,.txt"
											onChange={(e) => setAssetFile(e.target.files[0])}
											style={{
												padding: '0.5rem',
												border: '1px solid var(--border-color)',
												borderRadius: '4px',
												backgroundColor: 'var(--input-bg)',
												color: 'var(--text-primary)',
												width: '100%'
											}}
										/>
										<small style={{ color: 'var(--text-secondary)', marginTop: '0.25rem', display: 'block' }}>
											CSV format: IP,MAC,Vendor,Hostname,FirstSeen,Known,Zone,SwitchName,SwitchPort
										</small>
									</div>
								)}

								<div className={WatcherAssetsStyles.form_buttons_container}>
									<button
										className={WatcherAssetsStyles.form_button_submit}
										onClick={handleAddKnownAssets}
									>
										{assetEntryMode === 'single' ? 'Add Asset' : 'Upload File'}
									</button>
									<button
										className={WatcherAssetsStyles.form_button_cancel}
										onClick={() => {
											setShowAddKnownAssetsForm(false);
											setSingleAssetIP('');
											setSingleAssetMAC('');
											setSingleAssetVendor('');
											setSingleAssetHostname('');
											setSingleAssetZone('');
											setSingleAssetSwitchName('');
											setSingleAssetSwitchPort('');
											setAssetFile(null);
											setAssetEntryMode('single');
										}}
									>
										Cancel
									</button>
								</div>
							</div>
						)}

						{showEditKnownAssetsForm && userRole === 'admin' && editingAssetData && (
							<div className={WatcherAssetsStyles.add_net_zone_form_container}>
								<h4>Edit Known Asset</h4>
								
								{/* Row 1: IP Address and MAC Address */}
								<div className={WatcherAssetsStyles.form_row}>
									<div className={WatcherAssetsStyles.form_field}>
										<label htmlFor="editAssetIPInput">IP Address</label>
										<TextInput
											id="editAssetIPInput"
											placeholder="Enter IP Address"
											value={editAssetIP}
											onChange={(e) => setEditAssetIP(e.target.value)}
										/>
									</div>
									<div className={WatcherAssetsStyles.form_field}>
										<label htmlFor="editAssetMACInput">MAC Address</label>
										<TextInput
											id="editAssetMACInput"
											placeholder="Enter MAC Address"
											value={editAssetMAC}
											onChange={(e) => setEditAssetMAC(e.target.value)}
										/>
									</div>
								</div>

								{/* Row 2: Vendor and Hostname */}
								<div className={WatcherAssetsStyles.form_row}>
									<div className={WatcherAssetsStyles.form_field}>
										<label htmlFor="editAssetVendorInput">Vendor</label>
										<TextInput
											id="editAssetVendorInput"
											placeholder="Enter Vendor"
											value={editAssetVendor}
											onChange={(e) => setEditAssetVendor(e.target.value)}
										/>
									</div>
									<div className={WatcherAssetsStyles.form_field}>
										<label htmlFor="editAssetHostnameInput">Hostname</label>
										<TextInput
											id="editAssetHostnameInput"
											placeholder="Enter Hostname"
											value={editAssetHostname}
											onChange={(e) => setEditAssetHostname(e.target.value)}
										/>
									</div>
								</div>

								{/* Row 3: Device OS and Zone */}
								<div className={WatcherAssetsStyles.form_row}>
									<div className={WatcherAssetsStyles.form_field}>
										<label htmlFor="editAssetOSInput">Device OS</label>
										<TextInput
											id="editAssetOSInput"
											placeholder="Enter Device OS"
											value={editAssetOS}
											onChange={(e) => setEditAssetOS(e.target.value)}
										/>
									</div>
									<div className={WatcherAssetsStyles.form_field}>
										<label htmlFor="editAssetZoneInput">Zone</label>
										<TextInput
											id="editAssetZoneInput"
											placeholder="Enter Zone"
											value={editAssetZone}
											onChange={(e) => setEditAssetZone(e.target.value)}
										/>
									</div>
								</div>

								{/* Row 4: Switch Name and Switch Port */}
								<div className={WatcherAssetsStyles.form_row}>
									<div className={WatcherAssetsStyles.form_field}>
										<label htmlFor="editAssetSwitchNameInput">Switch Name</label>
										<TextInput
											id="editAssetSwitchNameInput"
											placeholder="Enter Switch Name"
											value={editAssetSwitchName}
											onChange={(e) => setEditAssetSwitchName(e.target.value)}
										/>
									</div>
									<div className={WatcherAssetsStyles.form_field}>
										<label htmlFor="editAssetSwitchPortInput">Switch Port</label>
										<TextInput
											id="editAssetSwitchPortInput"
											placeholder="Enter Switch Port"
											value={editAssetSwitchPort}
											onChange={(e) => setEditAssetSwitchPort(e.target.value)}
										/>
									</div>
								</div>
								
								{/* Row 5: Assigned Ports */}
								<div className={WatcherAssetsStyles.form_row}>
									<div className={WatcherAssetsStyles.form_field}>
										<label htmlFor="editAssetAssignedPortsInput">Assigned Ports</label>
										<TextInput
											id="editAssetAssignedPortsInput"
											placeholder="Enter ports separated by commas (e.g., 80,443,22)"
											value={editAssetAssignedPorts}
											onChange={(e) => {
												const value = e.target.value;
												// Only allow numbers, commas, and spaces
												const cleaned = value.replace(/[^0-9,\s]/g, '');
												setEditAssetAssignedPorts(cleaned);
											}}
											style={{
												borderColor: editAssetAssignedPorts && !validatePorts(editAssetAssignedPorts) ? 'red' : undefined
											}}
										/>
										{editAssetAssignedPorts && !validatePorts(editAssetAssignedPorts) && (
											<small style={{ color: 'red', fontSize: '0.8rem' }}>
												Please enter valid port numbers (1-65535) separated by commas
											</small>
										)}
									</div>
								</div>

								{/* Row 6: Note */}
								<div className={WatcherAssetsStyles.form_row}>
									<div className={WatcherAssetsStyles.form_field} style={{ gridColumn: '1 / -1' }}>
										<label htmlFor="editAssetNoteInput">Note</label>
										<TextArea
											id="editAssetNoteInput"
											placeholder="Enter note here..."
											value={editAssetNote}
											onChange={(e) => setEditAssetNote(e.target.value)}
											style={{ minHeight: '80px' }}
										/>
									</div>
								</div>

								<div className={WatcherAssetsStyles.form_buttons_container}>
									<button
										className={WatcherAssetsStyles.form_button_submit}
										onClick={handleEditKnownAssets}
									>
										Update Asset
									</button>
									<button
										className={WatcherAssetsStyles.form_button_cancel}
										onClick={() => {
											setShowEditKnownAssetsForm(false);
											setEditingAssetData(null);
											setEditAssetIP('');
											setEditAssetMAC('');
											setEditAssetVendor('');
											setEditAssetHostname('');
											setEditAssetOS('');
											setEditAssetZone('');
											setEditAssetSwitchName('');
											setEditAssetSwitchPort('');
											setEditAssetAssignedPorts('');
											setEditAssetNote('');
										}}
									>
										Cancel
									</button>
								</div>
							</div>
						)}

						<Table
							containerStyle={{ height: '100%', flex: '1 1 auto', overflow: 'auto' }}
							tableHead={
								<tr>
									{/* Add empty header cell for checkbox column */}
									{userRole === 'admin' && (
										<th style={{ width: '40px' }}></th>
									)}
									{userRole === 'admin' && <th style={{ width: '110px' }}>Action</th>}
									{watcherDataInitials.KAssetsCols &&
										watcherDataInitials.KAssetsCols.length > 0 &&
										watcherDataInitials.KAssetsCols.map((col, index) => (
											<SortableTh key={col + index} label={col} field={index} sort={knownSort} />
										))}
								</tr>
							}
							tableRow={
								knownAssetsTotalRows > 0 && knownAssetsRows.length > 0 ? (
									knownAssetsRows.map((rows, rowIndex) => (
										<tr key={`known-row-${rowIndex}`} className={getRowClass(rows, watcherDataInitials.KAssetsCols)}>
											{/* Keep individual checkbox for each row */}
											{userRole === 'admin' && (
												<td style={{ width: '40px' }}>
													<input
														type="checkbox"
														checked={selectedKnownAssets.has(rowIndex)}
														onChange={(e) => handleSelectKnownAsset(rowIndex, e.target.checked)}
														style={{ cursor: 'pointer' }}
													/>
												</td>
											)}
											{userRole === 'admin' && (
												<td style={{ width: '110px' }}>
													<button
														type='button'
														className={WatcherAssetsStyles.edit_known_button}
														onClick={() => handleEditKnownAsset(rows, rowIndex)}
													>
														Edit
													</button>
												</td>
											)}
											{rows && rows.length > 0 && 
												rows
													.slice(
														0,
														(watcherDataInitials.KAssetsCols && watcherDataInitials.KAssetsCols.length)
															? watcherDataInitials.KAssetsCols.length
															: rows.length
													)
													.map((cell, cellIndex) => (
														<td key={`known-cell-${rowIndex}-${cellIndex}`}>
															{renderCellContent(cell, cellIndex, watcherDataInitials.KAssetsCols, rows)}
														</td>
													))
											}
										</tr>
									))
								) : (
									<tr>
											<td
												colSpan={
													((watcherDataInitials && watcherDataInitials.KAssetsCols && watcherDataInitials.KAssetsCols.length) || 0) + (userRole === 'admin' ? 2 : 0)
												}
											style={{ textAlign: 'center' }}>
											No data found
										</td>
									</tr>
								)
							}
						/>

						<div className='paginationContainer'>
							<Pagination
								onPageChange={handleKnownAssetsPageChange}
								rowsPerPage={pageLimit}
								totalPageSize={knownAssetsTotalRows}
								pageNumber={pageNumberKnownAssets}
							/>
						</div>
					</div>

					<div className={WatcherAssetsStyles.table}>
						<div className={WatcherAssetsStyles.table_heading}>
							<div className={WatcherAssetsStyles.table_header}>
								<h3 className={WatcherAssetsStyles.table_header_text}>Detected Internal Assets</h3>
								
								{/* Add Move to Known Assets button when assets are selected */}
								{userRole === 'admin' && selectedDetectedAssets.size > 0 && (
									<button
										className={WatcherAssetsStyles.export_button}
										onClick={handleMoveToKnownAssets}
										style={{ backgroundColor: '#28a745', marginRight: '10px' }}
									>
										Move to Known Assets ({selectedDetectedAssets.size})
									</button>
								)}
								
								<div className={WatcherAssetsStyles.export_buttons}>
									<button
										className={WatcherAssetsStyles.export_button}
										onClick={() => {
											const filteredRows = watcherData.DAssetsRows.filter((row) =>
												searchDetectedAssetsKey
													? row.find((el) => String(el ?? '').toLowerCase().includes(String(searchDetectedAssetsKey ?? '').toLowerCase()))
													: true
											);
											exportToCSV(filteredRows, watcherData.DAssetsCols, generateFilename('Detected Assets'));
										}}
									>
										Export CSV
									</button>
									<button
										className={WatcherAssetsStyles.export_button}
										onClick={() => {
											const filteredRows = watcherData.DAssetsRows.filter((row) =>
												searchDetectedAssetsKey
													? row.find((el) => String(el ?? '').toLowerCase().includes(String(searchDetectedAssetsKey ?? '').toLowerCase()))
													: true
											);
											exportToPDF(filteredRows, watcherData.DAssetsCols, generateFilename('detected_assets'));
										}}
									>
										Export PDF
									</button>
								</div>
							</div>
							<TextInput
								icon='search'
								placeholder='Search Here'
								value={searchDetectedAssetsKey}
								onChange={handleDetectedAssetsSearchChange}
								style={{ width: '15rem' }}
							/>
						</div>

						{showAddNetZoneForm && userRole === 'admin' && (
							<div className={WatcherAssetsStyles.add_net_zone_form_container}>
								{/* Company selection for FLC users */}
								{userCompany === 'FLC' && availableCompanies.length > 0 && (
									<div>
										<label htmlFor="companySelect">Target Company</label>
										<select
											id="companySelect"
											value={selectedCompanyForNetZone}
											onChange={(e) => setSelectedCompanyForNetZone(e.target.value)}
											className={WatcherAssetsStyles.company_select}
										>
											{availableCompanies.map((company) => (
												<option key={company} value={company}>
													{company}
												</option>
											))}
										</select>
									</div>
								)}
								
								<div>
									<label htmlFor="netZoneNameInput">NetZone Name</label>
									<TextInput
										id="netZoneNameInput"
										placeholder="Enter NetZone Name"
										value={netZoneName}
										onChange={(e) => setNetZoneName(e.target.value)}
									/>
								</div>
								<div>
									<label htmlFor="firstIpInput">First IP Address</label>
									<TextInput
										id="firstIpInput"
										placeholder="Enter First IP"
										value={firstIp}
										onChange={(e) => setFirstIp(e.target.value)}
									/>
								</div>
								<div>
									<label htmlFor="lastIpInput">Last IP Address</label>
									<TextInput
										id="lastIpInput"
										placeholder="Enter Last IP"
										value={lastIp}
										onChange={(e) => setLastIp(e.target.value)}
									/>
								</div>
								<div className={WatcherAssetsStyles.form_buttons_container}>
									<button
										className={WatcherAssetsStyles.form_button_submit}
										onClick={handleAddNetZoneSubmit}
									>
										Add Zone
									</button>
									<button
										className={WatcherAssetsStyles.form_button_cancel}
										onClick={() => {
											setShowAddNetZoneForm(false);
											setNetZoneName('');
											setFirstIp('');
											setLastIp('');
											if (userCompany === 'FLC' && availableCompanies.length > 0) {
												setSelectedCompanyForNetZone(availableCompanies[0]);
											}
										}}
									>
										Cancel
									</button>
								</div>
							</div>
						)}

						<Table
							containerStyle={{ height: '100%', flex: '1 1 auto', overflow: 'auto' }}
							tableHead={
								<tr>
									{/* Add checkbox header for bulk selection */}
									{userRole === 'admin' && (
										<th style={{ width: '40px' }}>
											<input
												type="checkbox"
												checked={selectAllDetectedAssets}
												onChange={(e) => handleSelectAllDetectedAssets(e.target.checked)}
												style={{ cursor: 'pointer' }}
											/>
										</th>
									)}
									{userRole === 'admin' && <th style={{ width: '120px' }}>Action</th>}
									{watcherDataInitials &&
										watcherDataInitials.DAssetsCols &&
										watcherDataInitials.DAssetsCols.length > 0 &&
										watcherDataInitials.DAssetsCols.map((col, index) => (
											<SortableTh key={col + index} label={col} field={index} sort={detectedSort} />
										))}
								</tr>
							}
							tableRow={
								detectedAssetsTotalRows > 0 && detectedAssetsRows.length > 0 ? (
									detectedAssetsRows.map((rows, rowIndex) => {
										const asset = mapDetectedAssetRow(rows);
										const assetKey = `${selectedWatcher}:${asset.deviceIP || ''}:${asset.deviceMAC || ''}`;
										const isMarkKnownDisabled = !asset.deviceIP || !asset.deviceMAC || markKnownInProgress.has(assetKey);
										return (
										<tr key={`detected-row-${rowIndex}`}>
											{/* Add checkbox cell for individual selection */}
											{userRole === 'admin' && (
												<td style={{ width: '40px' }}>
													<input
														type="checkbox"
														checked={selectedDetectedAssets.has(rowIndex)}
														onChange={(e) => handleSelectDetectedAsset(rowIndex, e.target.checked)}
														style={{ cursor: 'pointer' }}
													/>
												</td>
											)}
											{userRole === 'admin' && (
												<td style={{ width: '120px' }}>
													<button
														type='button'
														className={WatcherAssetsStyles.make_known_button}
														onClick={() => handleMarkDetectedAssetKnown(rows)}
														disabled={isMarkKnownDisabled}
													>
														{markKnownInProgress.has(assetKey) ? 'Saving...' : 'Make Known'}
													</button>
												</td>
											)}
											{rows && rows.length > 0 && 
												rows.map((cell, cellIndex) => (
													<td key={`detected-cell-${rowIndex}-${cellIndex}`}>
														{renderCellContent(cell, cellIndex, watcherDataInitials.DAssetsCols, rows, true)}
													</td>
												))
											}
										</tr>
									);
								})
								) : (
									<tr>
											<td
												colSpan={
													((watcherDataInitials && watcherDataInitials.DAssetsCols && watcherDataInitials.DAssetsCols.length) || 0) + (userRole === 'admin' ? 2 : 0)
												}
											style={{ textAlign: 'center' }}>
											No data found
										</td>
									</tr>
								)
							}
						/>

						<div className='paginationContainer'>
							<Pagination
								onPageChange={handleDetectedAssetsPageChange}
								rowsPerPage={pageLimit}
								totalPageSize={detectedAssetsTotalRows}
								pageNumber={pageNumberDetectedAssets}
							/>
						</div>
					</div>
				</div>
			)}
		</Content>
	);
};

export default WatcherAssetsScreen;
